import { useState } from 'react';
import { ChevronLeft, ChevronRight, MapPin, Users } from 'lucide-react';

interface AdminCalendarProps {
  interventions: any[];
}

export default function AdminCalendar({ interventions }: AdminCalendarProps) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'month' | 'week'>('week');

  // Helpers
  const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay(); // 0 is Sunday
  
  const getStartOfWeek = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // adjust when day is sunday
    return new Date(d.setDate(diff));
  };

  const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  };

  const addMonths = (date: Date, months: number) => {
    const d = new Date(date);
    d.setMonth(d.getMonth() + months);
    return d;
  };

  const isSameDay = (d1: Date, d2: Date) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // Handlers
  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, -1));
    } else {
      setCurrentDate(addDays(currentDate, -7));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else {
      setCurrentDate(addDays(currentDate, 7));
    }
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Render Interventions for a specific day
  const renderDayInterventions = (dayDate: Date) => {
    const dayInterventions = interventions.filter(i => {
      const iDate = new Date(i.scheduled_at);
      return isSameDay(iDate, dayDate);
    });

    // Sort by time
    dayInterventions.sort((a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime());

    return dayInterventions.map(i => {
      const date = new Date(i.scheduled_at);
      const isUnassigned = !i.collaborator;
      
      return (
        <div key={i.id} className={`p-1.5 mb-1 text-[10px] rounded border-l-2 ${isUnassigned ? 'bg-red-50 border-red-500' : 'bg-gold-50 border-gold-500'}`}>
          <div className="font-bold flex items-center justify-between">
            <span>{date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })} - {i.duration_hours}h</span>
            <span className="truncate ml-1">{i.prestation?.client?.full_name}</span>
          </div>
          {i.collaborator ? (
            <div className="text-gray-600 truncate flex items-center gap-1 mt-0.5"><Users size={8}/> {i.collaborator.full_name}</div>
          ) : (
            <div className="text-red-500 font-bold truncate mt-0.5">Non assigné</div>
          )}
          {i.prestation?.client?.billing_details?.city && (
             <div className="text-gray-400 truncate mt-0.5 flex items-center gap-1"><MapPin size={8}/> {i.prestation.client.billing_details.city}</div>
          )}
        </div>
      );
    });
  };

  // Render Month Grid
  const renderMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    
    const daysInMonth = getDaysInMonth(year, month);
    let firstDay = getFirstDayOfMonth(year, month);
    if (firstDay === 0) firstDay = 7; // Convert Sunday(0) to 7 for Monday start
    
    const days = [];
    const weekDays = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

    // Headers
    const headers = weekDays.map(day => (
      <div key={day} className="text-center font-bold text-xs uppercase tracking-widest text-gray-500 py-2 border-b border-gray-100">
        {day}
      </div>
    ));

    // Empty cells before start of month
    for (let i = 1; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="bg-gray-50/50 min-h-[100px] border-b border-r border-gray-100"></div>);
    }

    // Days
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(year, month, i);
      const isToday = isSameDay(date, new Date());
      
      days.push(
        <div key={i} className={`min-h-[120px] p-1 border-b border-r border-gray-100 flex flex-col ${isToday ? 'bg-gold-50/30' : 'bg-white'}`}>
          <div className="text-right p-1">
            <span className={`inline-flex items-center justify-center w-6 h-6 text-xs rounded-full ${isToday ? 'bg-gold-500 text-white font-bold' : 'text-gray-500'}`}>
              {i}
            </span>
          </div>
          <div className="flex-1 overflow-y-auto hide-scrollbar flex flex-col gap-1 p-1">
            {renderDayInterventions(date)}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white border border-gray-100 rounded-sm shadow-sm overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-50">
          {headers}
        </div>
        <div className="grid grid-cols-7">
          {days}
        </div>
      </div>
    );
  };

  // Render Week Grid
  const renderWeek = () => {
    const startOfWeek = getStartOfWeek(currentDate);
    const weekDays = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
    
    const headers = weekDays.map((dayName, idx) => {
      const date = addDays(startOfWeek, idx);
      const isToday = isSameDay(date, new Date());
      return (
        <div key={idx} className="text-center py-3 border-b border-gray-100 bg-gray-50">
          <div className="text-xs uppercase tracking-widest text-gray-500 font-bold">{dayName}</div>
          <div className={`text-lg mt-1 ${isToday ? 'text-gold-600 font-bold' : 'text-black-deep'}`}>
            {date.getDate()}
          </div>
        </div>
      );
    });

    const columns = weekDays.map((_, idx) => {
      const date = addDays(startOfWeek, idx);
      const isToday = isSameDay(date, new Date());
      return (
        <div key={idx} className={`min-h-[400px] border-r border-gray-100 p-2 flex flex-col gap-2 ${isToday ? 'bg-gold-50/10' : 'bg-white'}`}>
          {renderDayInterventions(date)}
        </div>
      );
    });

    return (
      <div className="bg-white border border-gray-100 rounded-sm shadow-sm overflow-hidden flex flex-col">
        <div className="grid grid-cols-7">
          {headers}
        </div>
        <div className="grid grid-cols-7 flex-1">
          {columns}
        </div>
      </div>
    );
  };

  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const monthName = monthNames[currentDate.getMonth()];
  const year = currentDate.getFullYear();
  
  let headerTitle = '';
  if (viewMode === 'month') {
    headerTitle = `${monthName} ${year}`;
  } else {
    const start = getStartOfWeek(currentDate);
    const end = addDays(start, 6);
    if (start.getMonth() === end.getMonth()) {
       headerTitle = `${start.getDate()} - ${end.getDate()} ${monthNames[start.getMonth()]} ${year}`;
    } else {
       headerTitle = `${start.getDate()} ${monthNames[start.getMonth()]} - ${end.getDate()} ${monthNames[end.getMonth()]} ${year}`;
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Calendar Toolbar */}
      <div className="flex justify-between items-center bg-white p-4 rounded-sm border border-gray-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-sm">
            <button onClick={() => setViewMode('week')} className={`px-3 py-1 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${viewMode === 'week' ? 'bg-white shadow text-black-deep' : 'text-gray-500 hover:text-black-deep'}`}>Semaine</button>
            <button onClick={() => setViewMode('month')} className={`px-3 py-1 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors ${viewMode === 'month' ? 'bg-white shadow text-black-deep' : 'text-gray-500 hover:text-black-deep'}`}>Mois</button>
          </div>
          <button onClick={handleToday} className="px-3 py-1 text-xs font-bold uppercase tracking-widest text-gold-600 hover:bg-gold-50 rounded-sm transition-colors border border-gold-200">
            Aujourd'hui
          </button>
        </div>
        
        <div className="flex items-center gap-4">
          <h2 className="font-serif text-xl w-48 text-center">{headerTitle}</h2>
          <div className="flex gap-1">
            <button onClick={handlePrev} className="p-1 hover:bg-gray-100 rounded-sm text-gray-500 transition-colors"><ChevronLeft size={20}/></button>
            <button onClick={handleNext} className="p-1 hover:bg-gray-100 rounded-sm text-gray-500 transition-colors"><ChevronRight size={20}/></button>
          </div>
        </div>
      </div>

      {/* Calendar Grid */}
      {viewMode === 'month' ? renderMonth() : renderWeek()}
      
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
